// Editor canvas rendering. Single `render(ctx, state)` entry point.
//
// Layer order is load-bearing — each draw* function's place in the
// stack matters. Don't reorder without testing against authored
// geometry. The interaction-state-dependent bits (hover, snap target,
// marquee, crosshair, calibration overlay) gracefully no-op when their
// state fields are null, so this same renderer works for the read-only
// viewer (phase 4a) and the full editor (4b+).

import { densifyClosedLoop, densifyOpenPath, curvePointAt, curvePointAtOpen, buildVariableWidthEdgesClosed, buildVariableWidthEdgesOpen } from '../../shared/spline.js';
import { markerLineEndpoints, sectorOfPoint, trackPerpendicularAt, SECTOR_COLORS } from '../../shared/markers.js';
import { SURFACE_COLORS, CURB_COLORS, TARMAC_COLORS, SURFACE_PRIORITY, patchLayer } from '../../shared/surfaces.js';
import { buildPitBoxes } from '../../shared/track-geometry.js';
import {
  activeWaypoints, activeAlpha, activeIsClosed, hasActiveEntity,
  isSelected, dimColor, hexWithAlpha, trackAlpha,
} from './state.js';
import { worldToScreen, screenToWorld } from './transforms.js';

// Background image cache. Single-slot keyed by data URL — recreates the
// HTMLImageElement only when the URL changes. Async loads trigger the
// onBgImageLoad callback so the host can re-render.
const _bgImageCache = { dataUrl: null, img: null, error: false, onLoad: null };

function getCachedBgImage(dataUrl, onLoad) {
  if (!dataUrl) return null;
  if (_bgImageCache.dataUrl === dataUrl) {
    return _bgImageCache.error ? null : _bgImageCache.img;
  }
  const img = new Image();
  _bgImageCache.dataUrl = dataUrl;
  _bgImageCache.img = img;
  _bgImageCache.error = false;
  _bgImageCache.onLoad = onLoad ?? null;
  img.onload = () => _bgImageCache.onLoad?.();
  img.onerror = () => { _bgImageCache.error = true; };
  img.src = dataUrl;
  return img;
}

export function render(ctx, state, { onBgImageLoad } = {}) {
  const canvas = ctx.canvas;
  const w = canvas.width, h = canvas.height;
  if (w === 0 || h === 0) return;

  const w2s = (x, y) => worldToScreen(state, canvas, x, y);

  ctx.fillStyle = '#0c0f12';
  ctx.fillRect(0, 0, w, h);

  state._trackEdges = snapToEdgeActive(state) ? computeTrackEdges(state) : null;

  drawBackgroundImage(ctx, state, w2s, onBgImageLoad);
  drawGrid(ctx, state, canvas);
  drawAxes(ctx, w2s);
  // Below-strip patches first (grass / gravel / sand). Off-track
  // surfaces — sit beneath the racing-surface group.
  drawSurfacePatches(ctx, state, w2s, 'below');
  // Merge patches (tarmac) before curbs and the strip so both paint
  // over tarmac patches in the overlap. Past the strip, tarmac
  // runoff stays visible (drawn after `below`).
  drawSurfacePatches(ctx, state, w2s, 'merge');
  // Above-strip patches (curbs) sit on top of tarmac patches but
  // BELOW the strip + pit lane — curbs that bleed onto the racing
  // surface get covered by it, matching how a real curb sits at the
  // track edge rather than on the asphalt.
  drawSurfacePatches(ctx, state, w2s, 'above');
  drawPitLaneTransitions(ctx, state, w2s);
  drawPitLane(ctx, state, w2s);
  drawPitBoxes(ctx, state, w2s);
  drawTrackPreview(ctx, state, w2s);
  drawTrackEdgeGuides(ctx, state, w2s);
  drawMarkers(ctx, state, w2s);
  drawPitMarkers(ctx, state, w2s);
  // Walls AFTER patches + markers — they obscure painted lines they
  // physically cover. Editing UI (waypoints, hover, snap, marquee,
  // crosshair, calibration) still draws on top.
  drawWalls(ctx, state, w2s);
  // Active surface patch on top of all content so the user can
  // always see the patch they're editing — even when its natural
  // layer would have it under the strip or behind a wall.
  drawActiveSurfacePatch(ctx, state, w2s);
  // Racing line — drawn after walls so a wall that crosses the line
  // doesn't obscure it (the line is an authoring aid, not a physical
  // object). Dimmed faintly when not in racingline mode so it stays
  // visible as reference but doesn't compete with the active mode.
  drawRacingLine(ctx, state, w2s);
  // Labels — corner badges + area banners. Authoring-only, dimmed
  // outside labels mode so they stay visible as reference but yield
  // visual priority to whatever the user is editing.
  drawLabels(ctx, state, w2s);
  drawWaypoints(ctx, state, w2s);
  drawHover(ctx, state, w2s);
  drawSnapTarget(ctx, state, w2s);
  drawMarquee(ctx, state);
  drawCrosshair(ctx, state, canvas);
  drawCalibrationOverlay(ctx, state, canvas, w2s);
}

// ---------- TRACK EDGE COMPUTATION (cached for snap + edge guides) ----------
function snapToEdgeActive(state) {
  if (state.mode === 'walls') return true;
  if (state.mode === 'surfaces' && state.activePatchIdx >= 0) {
    const patch = state.trackData.surfacePatches[state.activePatchIdx];
    return patch?.surface === 'curb';
  }
  if (state.mode === 'pitlane' && state.trackData.pitLane) return true;
  return false;
}

function computeTrackEdges(state) {
  const wps = state.trackData.trackline.waypoints;
  if (wps.length < 3) return null;
  const half = state.trackData.trackline.width / 2;
  const dense = densifyClosedLoop(wps, 24, trackAlpha(state));
  const inner = [], outer = [];
  for (let i = 0; i < dense.length; i++) {
    const a = dense[i];
    const b = dense[(i + 1) % dense.length];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    const nx = -dy / len, ny = dx / len;
    inner.push({ x: a.x - nx * half, y: a.y - ny * half, perpX: -nx, perpY: -ny, side: 'inner' });
    outer.push({ x: a.x + nx * half, y: a.y + ny * half, perpX:  nx, perpY:  ny, side: 'outer' });
  }
  return { inner, outer };
}

// ---------- BACKGROUND IMAGE ----------
function drawBackgroundImage(ctx, state, w2s, onLoad) {
  const bg = state.trackData.backgroundImage;
  if (!bg) return;
  const img = getCachedBgImage(bg.dataUrl, onLoad);
  if (!img || !img.complete || img.naturalWidth === 0) return;

  const mpp = bg.metersPerPixel || 0.5;
  const widthM  = img.naturalWidth  * mpp;
  const heightM = img.naturalHeight * mpp;
  const center = w2s(bg.centerX || 0, bg.centerY || 0);
  const wPx = widthM  * state.camera.zoom;
  const hPx = heightM * state.camera.zoom;

  ctx.save();
  ctx.globalAlpha = bg.opacity ?? 0.6;
  ctx.translate(center.x, center.y);
  ctx.rotate(bg.rotation || 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, -wPx / 2, -hPx / 2, wPx, hPx);
  ctx.restore();
}

// ---------- GRID + AXES ----------
function drawGrid(ctx, state, canvas) {
  const w = canvas.width, h = canvas.height;
  const z = state.camera.zoom;

  // Adaptive: keep grid lines roughly 30-90 px apart on screen.
  let step = 1;
  while (step * z < 30) step *= 2;
  while (step * z > 90) step /= 2;
  if (step < 1) step = Math.max(0.5, step);
  const major = step * 5;

  const tl = screenToWorld(state, canvas, 0, 0);
  const br = screenToWorld(state, canvas, w, h);

  ctx.strokeStyle = '#161a1e';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = Math.ceil(tl.x / step) * step; x <= br.x; x += step) {
    const sx = (x - state.camera.x) * z + w / 2;
    ctx.moveTo(sx, 0); ctx.lineTo(sx, h);
  }
  for (let y = Math.ceil(tl.y / step) * step; y <= br.y; y += step) {
    const sy = (y - state.camera.y) * z + h / 2;
    ctx.moveTo(0, sy); ctx.lineTo(w, sy);
  }
  ctx.stroke();

  ctx.strokeStyle = '#23282e';
  ctx.beginPath();
  for (let x = Math.ceil(tl.x / major) * major; x <= br.x; x += major) {
    const sx = (x - state.camera.x) * z + w / 2;
    ctx.moveTo(sx, 0); ctx.lineTo(sx, h);
  }
  for (let y = Math.ceil(tl.y / major) * major; y <= br.y; y += major) {
    const sy = (y - state.camera.y) * z + h / 2;
    ctx.moveTo(0, sy); ctx.lineTo(w, sy);
  }
  ctx.stroke();
}

function drawAxes(ctx, w2s) {
  const o = w2s(0, 0);
  ctx.strokeStyle = '#3a4046';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(o.x - 9, o.y); ctx.lineTo(o.x + 9, o.y);
  ctx.moveTo(o.x, o.y - 9); ctx.lineTo(o.x, o.y + 9);
  ctx.stroke();
  ctx.fillStyle = '#3a4046';
  ctx.font = '9px JetBrains Mono';
  ctx.fillText('0,0', o.x + 12, o.y - 4);
}

// ---------- RACING LINE ----------
// Draws the authored racing line as a faint cyan polyline in any
// mode (so it stays visible as reference) and brighter in racingline
// mode. Two-point or shorter input renders as a dashed straight line
// preview, mirroring drawTrackPreview's behavior at low waypoint
// counts.
function drawRacingLine(ctx, state, w2s) {
  const rl = state.trackData.racingLine;
  if (!rl) return;
  const wps = rl.waypoints || [];
  if (wps.length < 2) return;
  const isActive = state.mode === 'racingline';
  const stroke = isActive ? 'rgba(120, 220, 240, 0.95)' : 'rgba(120, 220, 240, 0.45)';
  const lineW  = isActive ? 1.6 : 1.0;

  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.lineWidth   = lineW;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();

  if (wps.length < 4) {
    // Catmull-Rom closed loop needs at least 4 control points;
    // fall back to a polyline preview while the user is still
    // dropping points.
    const a = w2s(wps[0].x, wps[0].y);
    ctx.moveTo(a.x, a.y);
    for (let i = 1; i < wps.length; i++) {
      const p = w2s(wps[i].x, wps[i].y);
      ctx.lineTo(p.x, p.y);
    }
  } else {
    const dense = densifyClosedLoop(wps, 14, rl.alpha ?? 0.5);
    const f = w2s(dense[0].x, dense[0].y);
    ctx.moveTo(f.x, f.y);
    for (let i = 1; i < dense.length; i++) {
      const p = w2s(dense[i].x, dense[i].y);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
  }
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

// ---------- TRACK PREVIEW ----------
function drawTrackPreview(ctx, state, w2s) {
  const wps = state.trackData.trackline.waypoints;
  const dim = state.mode !== 'track';

  if (wps.length < 3) {
    if (wps.length === 2) {
      const a = w2s(wps[0].x, wps[0].y);
      const b = w2s(wps[1].x, wps[1].y);
      ctx.strokeStyle = dim ? 'rgba(229, 75, 60, 0.15)' : 'rgba(229, 75, 60, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    return;
  }

  const SUBS = 14;
  const tw = state.trackData.trackline.width;
  const { dense, inner, outer } = buildVariableWidthEdgesClosed(wps, tw, trackAlpha(state), SUBS);

  // Surface fill — closed annulus (outer ring + inner ring with
  // evenodd). NOT the open-ribbon fill that pit lane uses; for a
  // closed loop, the outer-forward + inner-backward approach leaves a
  // one-densification-step wedge of the surface unfilled at the
  // wrap-around seam (between dense[N-1] and dense[0]).
  ctx.fillStyle = dim ? 'rgba(70, 76, 82, 0.25)' : 'rgba(70, 76, 82, 0.55)';
  fillAnnulus(ctx, w2s, outer, inner);

  if (state.trackData.sectors?.length === 2) {
    drawSectorTints(ctx, state, w2s, dense, inner, outer, SUBS);
  }

  // Centerline (red spline)
  ctx.strokeStyle = dim ? 'rgba(229, 75, 60, 0.35)' : '#e54b3c';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  const sf = w2s(dense[0].x, dense[0].y);
  ctx.moveTo(sf.x, sf.y);
  for (let i = 1; i < dense.length; i++) {
    const s = w2s(dense[i].x, dense[i].y);
    ctx.lineTo(s.x, s.y);
  }
  ctx.closePath();
  ctx.stroke();

  drawDirectionArrows(ctx, state, w2s, dense);
}

// Fill a closed-loop annulus from two same-length, same-length-as-the-
// dense-loop polylines. Each is emitted as its own closed sub-path;
// the evenodd fill rule then fills the area between them regardless
// of winding direction. Use this for the track surface and any other
// "closed ring" fills. For OPEN ribbons (pit lane, sectors with two
// endpoints) use fillRibbon instead.
function fillAnnulus(ctx, w2s, outerList, innerList) {
  if (outerList.length < 3 || innerList.length !== outerList.length) return;
  ctx.beginPath();
  let p = w2s(outerList[0].x, outerList[0].y);
  ctx.moveTo(p.x, p.y);
  for (let i = 1; i < outerList.length; i++) {
    p = w2s(outerList[i].x, outerList[i].y);
    ctx.lineTo(p.x, p.y);
  }
  ctx.closePath();
  p = w2s(innerList[0].x, innerList[0].y);
  ctx.moveTo(p.x, p.y);
  for (let i = 1; i < innerList.length; i++) {
    p = w2s(innerList[i].x, innerList[i].y);
    ctx.lineTo(p.x, p.y);
  }
  ctx.closePath();
  ctx.fill('evenodd');
}

// Fill an open ribbon polygon from two equal-length polylines:
// `outerList` walked forward, then `innerList` walked backward. The
// resulting polygon caps cleanly at both endpoints. Used for the pit
// lane band and sector slices.
function fillRibbon(ctx, w2s, outerList, innerList) {
  if (outerList.length === 0 || innerList.length !== outerList.length) return;
  ctx.beginPath();
  const f = w2s(outerList[0].x, outerList[0].y);
  ctx.moveTo(f.x, f.y);
  for (let i = 1; i < outerList.length; i++) {
    const s = w2s(outerList[i].x, outerList[i].y);
    ctx.lineTo(s.x, s.y);
  }
  for (let i = innerList.length - 1; i >= 0; i--) {
    const s = w2s(innerList[i].x, innerList[i].y);
    ctx.lineTo(s.x, s.y);
  }
  ctx.closePath();
  ctx.fill();
}

function drawDirectionArrows(ctx, state, w2s, dense) {
  const isCCW = state.trackData.direction === 'ccw';
  const arrowCount = 8;
  if (dense.length < arrowCount * 3) return;
  const step = Math.floor(dense.length / arrowCount);
  const arrowLenPx = 14;

  ctx.strokeStyle = state.mode === 'track' ? '#fff' : 'rgba(255, 255, 255, 0.4)';
  ctx.fillStyle   = state.mode === 'track' ? '#fff' : 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';

  for (let i = 0; i < arrowCount; i++) {
    const k = i * step;
    const a = dense[k];
    const b = dense[(k + 1) % dense.length];
    let dx = b.x - a.x, dy = b.y - a.y;
    if (isCCW) { dx = -dx; dy = -dy; }
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    const nx = dx / len, ny = dy / len;
    const sa = w2s(a.x, a.y);
    const fx = sa.x + nx * arrowLenPx;
    const fy = sa.y + ny * arrowLenPx;
    const wingLen = arrowLenPx * 0.55;
    const wingAngle = Math.PI * 140 / 180;
    const baseAngle = Math.atan2(ny, nx);
    const lwx = fx + Math.cos(baseAngle + wingAngle) * wingLen;
    const lwy = fy + Math.sin(baseAngle + wingAngle) * wingLen;
    const rwx = fx + Math.cos(baseAngle - wingAngle) * wingLen;
    const rwy = fy + Math.sin(baseAngle - wingAngle) * wingLen;
    ctx.beginPath();
    ctx.moveTo(lwx, lwy);
    ctx.lineTo(fx, fy);
    ctx.lineTo(rwx, rwy);
    ctx.stroke();
  }
}

// Walk the dense closed loop and fill each sector's stretch as a
// polygon using the inner/outer edge polylines (variable-width-aware).
// Indices are tracked rather than copying point arrays so we can reuse
// the cached inner/outer that already factor in per-waypoint widths.
function drawSectorTints(ctx, state, w2s, dense, inner, outer, SUBS) {
  const denseToSegT = k => ({ segmentIdx: Math.floor(k / SUBS), t: (k % SUBS) / SUBS });
  const sf = state.trackData.startFinish;
  const sectors = state.trackData.sectors;
  const numWps = state.trackData.trackline.waypoints.length;
  const n = dense.length;

  // Build runs as { sector, start, end } index ranges (inclusive).
  // Each run shares its boundary index with the next so the polygons
  // abut without a gap.
  const runs = [];
  let curSec = sectorOfPoint(denseToSegT(0).segmentIdx, denseToSegT(0).t, sectors, sf, numWps);
  let curStart = 0;
  for (let i = 1; i < n; i++) {
    const st = denseToSegT(i);
    const sec = sectorOfPoint(st.segmentIdx, st.t, sectors, sf, numWps);
    if (sec !== curSec) {
      runs.push({ sector: curSec, start: curStart, end: i });
      curSec = sec;
      curStart = i;
    }
  }
  runs.push({ sector: curSec, start: curStart, end: n - 1 });

  // Wraparound handling at the dense seam (between dense[n-1] and
  // dense[0]):
  //   • If first.sector === last.sector → the seam sits inside one
  //     sector; merge into one polygon spanning both ranges.
  //   • Otherwise → S/F (or some sector boundary) lives BETWEEN
  //     dense[n-1] and dense[0]. The default polygons end at outer[n-1]
  //     and start at outer[0], leaving the seam wedge covered by
  //     neither polygon (visible as a gray slot through the tint right
  //     at S/F). Fix: extend the last run by the single vertex 0 so
  //     its cross-section abuts the first run's cross-section at
  //     dense[0]. Adjacent sectors share that cross-section the same
  //     way non-seam boundaries already do.
  if (runs.length >= 2 && runs[0].sector === runs[runs.length - 1].sector) {
    const last = runs.pop();
    runs[0] = { sector: runs[0].sector, ranges: [
      { start: last.start, end: n - 1 },
      { start: 0, end: runs[0].end },
    ]};
  } else if (runs.length >= 2) {
    const lastIdx = runs.length - 1;
    const last = runs[lastIdx];
    runs[lastIdx] = {
      sector: last.sector,
      ranges: [
        { start: last.start, end: n - 1 },
        { start: 0, end: 0 },
      ],
    };
  }

  for (const run of runs) {
    ctx.fillStyle = SECTOR_COLORS[run.sector].tint;
    ctx.beginPath();
    const ranges = run.ranges ?? [{ start: run.start, end: run.end }];
    let first = true;
    // Outer forward across each sub-range.
    for (const r of ranges) {
      for (let i = r.start; i <= r.end; i++) {
        const s = w2s(outer[i].x, outer[i].y);
        if (first) { ctx.moveTo(s.x, s.y); first = false; }
        else ctx.lineTo(s.x, s.y);
      }
    }
    // Inner backward across reversed sub-ranges.
    for (let ri = ranges.length - 1; ri >= 0; ri--) {
      const r = ranges[ri];
      for (let i = r.end; i >= r.start; i--) {
        const s = w2s(inner[i].x, inner[i].y);
        ctx.lineTo(s.x, s.y);
      }
    }
    ctx.closePath();
    ctx.fill();
  }
}

// ---------- TRACK EDGE GUIDES (snap dashed lines) ----------
function drawTrackEdgeGuides(ctx, state, w2s) {
  if (!snapToEdgeActive(state)) return;
  const edges = state._trackEdges;
  if (!edges) return;

  ctx.strokeStyle = 'rgba(108, 223, 108, 0.35)';
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);

  for (const list of [edges.inner, edges.outer]) {
    if (list.length < 2) continue;
    ctx.beginPath();
    const f = w2s(list[0].x, list[0].y);
    ctx.moveTo(f.x, f.y);
    for (let i = 1; i < list.length; i++) {
      const s = w2s(list[i].x, list[i].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.closePath();
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

// ---------- WALLS ----------
function drawWalls(ctx, state, w2s) {
  const walls = state.trackData.walls || [];
  for (let wi = 0; wi < walls.length; wi++) {
    const wall = walls[wi];
    const isActive = state.mode === 'walls' && state.activeWallIdx === wi;
    drawOneWall(ctx, state, w2s, wall, isActive);
  }
}

function drawOneWall(ctx, state, w2s, wall, isActive) {
  const wps = wall.waypoints;
  if (wps.length < 2) {
    if (wps.length === 1 && isActive) {
      const s = w2s(wps[0].x, wps[0].y);
      ctx.fillStyle = 'rgba(154, 157, 163, 0.5)';
      ctx.beginPath();
      ctx.arc(s.x, s.y, 4, 0, 2 * Math.PI);
      ctx.fill();
    }
    return;
  }

  const closed = wall.closed && wps.length >= 3;
  const alpha = wall.alpha ?? 0.5;
  const thickness = wall.thickness ?? 0.5;
  const dense = closed ? densifyClosedLoop(wps, 14, alpha) : densifyOpenPath(wps, 14, alpha);

  const widthPx = Math.max(2, thickness * state.camera.zoom);
  ctx.strokeStyle = isActive ? '#c9ccd1' : 'rgba(201, 204, 209, 0.35)';
  ctx.lineWidth = widthPx;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  const f = w2s(dense[0].x, dense[0].y);
  ctx.moveTo(f.x, f.y);
  for (let i = 1; i < dense.length; i++) {
    const s = w2s(dense[i].x, dense[i].y);
    ctx.lineTo(s.x, s.y);
  }
  if (closed) ctx.closePath();
  ctx.stroke();
}

// ---------- PIT LANE + RIBBONS ----------
function pitLaneStartTangent(state) {
  const pl = state.trackData.pitLane;
  if (!pl || pl.waypoints.length < 2) return null;
  const a = pl.alpha ?? 0.5;
  const p1 = curvePointAtOpen(pl.waypoints, 0, 0,    a);
  const p2 = curvePointAtOpen(pl.waypoints, 0, 0.02, a);
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  return len > 1e-9 ? { x: dx / len, y: dy / len } : null;
}
function pitLaneEndTangent(state) {
  const pl = state.trackData.pitLane;
  if (!pl || pl.waypoints.length < 2) return null;
  const a = pl.alpha ?? 0.5;
  const lastSeg = pl.waypoints.length - 2;
  const p1 = curvePointAtOpen(pl.waypoints, lastSeg, 0.98, a);
  const p2 = curvePointAtOpen(pl.waypoints, lastSeg, 1,    a);
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  return len > 1e-9 ? { x: dx / len, y: dy / len } : null;
}

function trackForwardTangentAt(state, segIdx, t) {
  const wps = state.trackData.trackline.waypoints;
  if (wps.length < 3) return { x: 1, y: 0 };
  const a = trackAlpha(state);
  const eps = 0.005;
  const t1 = Math.max(0, t - eps);
  const t2 = Math.min(1 - 1e-6, t + eps);
  const p1 = curvePointAt(wps, segIdx, t1, a);
  const p2 = curvePointAt(wps, segIdx, t2, a);
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  return len > 1e-9 ? { x: dx / len, y: dy / len } : { x: 1, y: 0 };
}

function cubicBezierPoint(P0, P1, P2, P3, t) {
  const u = 1 - t, u2 = u * u, u3 = u2 * u;
  const t2 = t * t, t3 = t2 * t;
  return {
    x: u3*P0.x + 3*u2*t*P1.x + 3*u*t2*P2.x + t3*P3.x,
    y: u3*P0.y + 3*u2*t*P1.y + 3*u*t2*P2.y + t3*P3.y,
  };
}
function densifyCubicBezier(P0, P1, P2, P3, subs = 28) {
  const out = [];
  for (let i = 0; i <= subs; i++) out.push(cubicBezierPoint(P0, P1, P2, P3, i / subs));
  return out;
}

function drawPitLaneTransitions(ctx, state, w2s) {
  const pl = state.trackData.pitLane;
  if (!pl || pl.waypoints.length < 2) return;
  if (state.trackData.trackline.waypoints.length < 3) return;

  const isActive = state.mode === 'pitlane';
  const fillColor = isActive ? 'rgba(58, 64, 70, 0.95)' : 'rgba(58, 64, 70, 0.55)';
  const edgeColor = isActive ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.45)';
  const wps = state.trackData.trackline.waypoints;
  const a = trackAlpha(state);

  if (pl.entry) {
    const trackPoint = curvePointAt(wps, pl.entry.segmentIdx, pl.entry.t, a);
    const trackTan   = trackForwardTangentAt(state, pl.entry.segmentIdx, pl.entry.t);
    const plStart    = pl.waypoints[0];
    const plStartTan = pitLaneStartTangent(state);
    if (plStartTan) {
      drawTransitionRibbon(ctx, state, w2s, trackPoint, trackTan, plStart, plStartTan, pl.width, fillColor, edgeColor);
    }
  }
  if (pl.exit) {
    const plEnd      = pl.waypoints[pl.waypoints.length - 1];
    const plEndTan   = pitLaneEndTangent(state);
    const trackPoint = curvePointAt(wps, pl.exit.segmentIdx, pl.exit.t, a);
    const trackTan   = trackForwardTangentAt(state, pl.exit.segmentIdx, pl.exit.t);
    if (plEndTan) {
      drawTransitionRibbon(ctx, state, w2s, plEnd, plEndTan, trackPoint, trackTan, pl.width, fillColor, edgeColor);
    }
  }
}

function drawTransitionRibbon(ctx, state, w2s, P0, T0, P3, T3, widthWorld, fillColor, edgeColor) {
  const chord = Math.hypot(P3.x - P0.x, P3.y - P0.y);
  if (chord < 0.5) return;
  const scale = chord / 3;
  const P1 = { x: P0.x + T0.x * scale, y: P0.y + T0.y * scale };
  const P2 = { x: P3.x - T3.x * scale, y: P3.y - T3.y * scale };

  const dense = densifyCubicBezier(P0, P1, P2, P3, 28);
  const widthPx = Math.max(2, widthWorld * state.camera.zoom);

  ctx.lineWidth = widthPx;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = fillColor;
  ctx.beginPath();
  const f = w2s(dense[0].x, dense[0].y);
  ctx.moveTo(f.x, f.y);
  for (let i = 1; i < dense.length; i++) {
    const s = w2s(dense[i].x, dense[i].y);
    ctx.lineTo(s.x, s.y);
  }
  ctx.stroke();

  const half = widthWorld / 2;
  const inner = [], outer = [];
  for (let i = 0; i < dense.length - 1; i++) {
    const a = dense[i], b = dense[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    const nx = -dy / len, ny = dx / len;
    inner.push({ x: a.x - nx * half, y: a.y - ny * half });
    outer.push({ x: a.x + nx * half, y: a.y + ny * half });
  }
  if (dense.length >= 2) {
    const a = dense[dense.length - 2], b = dense[dense.length - 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len > 1e-9) {
      const nx = -dy / len, ny = dx / len;
      inner.push({ x: b.x - nx * half, y: b.y - ny * half });
      outer.push({ x: b.x + nx * half, y: b.y + ny * half });
    }
  }

  ctx.lineWidth = 1.5;
  ctx.lineCap = 'butt';
  ctx.strokeStyle = edgeColor;
  for (const list of [inner, outer]) {
    if (list.length < 2) continue;
    ctx.beginPath();
    const sf = w2s(list[0].x, list[0].y);
    ctx.moveTo(sf.x, sf.y);
    for (let i = 1; i < list.length; i++) {
      const s = w2s(list[i].x, list[i].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
  }
}

function drawPitLane(ctx, state, w2s) {
  const pl = state.trackData.pitLane;
  if (!pl || pl.waypoints.length < 2) return;

  const isActive = state.mode === 'pitlane';
  const { inner, outer } = buildVariableWidthEdgesOpen(
    pl.waypoints, pl.width ?? 4.0, pl.alpha ?? 0.5, 14,
  );

  // Variable-width fill — open ribbon polygon (outer forward, then
  // inner reverse). The end-caps come for free from the polygon close.
  ctx.fillStyle = isActive ? 'rgba(58, 64, 70, 0.95)' : 'rgba(58, 64, 70, 0.55)';
  fillRibbon(ctx, w2s, outer, inner);

  // White edge lines along inner + outer rails — same arrays we filled
  // with, so any per-waypoint width override is already baked in.
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'butt';
  ctx.strokeStyle = isActive ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.45)';
  for (const list of [inner, outer]) {
    if (list.length < 2) continue;
    ctx.beginPath();
    const sf = w2s(list[0].x, list[0].y);
    ctx.moveTo(sf.x, sf.y);
    for (let i = 1; i < list.length; i++) {
      const s = w2s(list[i].x, list[i].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
  }
}

// Auto-generated pit boxes — derived from pl.boxSide + speed-zone arcs.
// Editor shows the FULL maximum row at all times so the author can see
// the capacity their zone produces; sim slices to current car count.
// Tarmac fill + white separator strokes between adjacent boxes (the
// inner/outer rails coincide with the lane edge + outer line so we
// only stroke the cross-lane separators, not the perimeter).
function drawPitBoxes(ctx, state, w2s) {
  const pl = state.trackData.pitLane;
  if (!pl || !pl.boxSide || pl.waypoints.length < 2) return;
  const edges = buildVariableWidthEdgesOpen(
    pl.waypoints, pl.width ?? 4.0, pl.alpha ?? 0.5, 14,
  );
  const pitLaneShape = {
    dense: edges.dense,
    halfWidths: edges.halfWidths,
    halfWidth: (pl.width ?? 4.0) / 2,
  };
  const pb = buildPitBoxes(pl, pitLaneShape);
  if (!pb) return;

  const isActive = state.mode === 'pitlane';
  const tarmacFill = isActive ? '#3a4046' : 'rgba(58, 64, 70, 0.7)';
  const lineColor  = isActive ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.5)';

  // Tarmac fill — polygon = innerEdge forward + outerEdge reversed.
  // No outer rail stroke (per user choice — boxes look like ticks +
  // tarmac, not enclosed rectangles).
  ctx.fillStyle = tarmacFill;
  for (const box of pb.boxes) {
    ctx.beginPath();
    const inner = box.innerEdge;
    const outer = box.outerEdge;
    const i0 = w2s(inner[0].x, inner[0].y);
    ctx.moveTo(i0.x, i0.y);
    for (let i = 1; i < inner.length; i++) {
      const s = w2s(inner[i].x, inner[i].y);
      ctx.lineTo(s.x, s.y);
    }
    for (let i = outer.length - 1; i >= 0; i--) {
      const s = w2s(outer[i].x, outer[i].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.closePath();
    ctx.fill();
  }

  // Perpendicular dividers between team boxes (back edge of each box,
  // plus the front edge of the final box).
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  for (let bi = 0; bi < pb.boxes.length; bi++) {
    const box = pb.boxes[bi];
    const ai = w2s(box.innerEdge[0].x, box.innerEdge[0].y);
    const ao = w2s(box.outerEdge[0].x, box.outerEdge[0].y);
    ctx.moveTo(ai.x, ai.y);
    ctx.lineTo(ao.x, ao.y);
    if (bi === pb.boxes.length - 1) {
      const li = box.innerEdge.length - 1;
      const lo = box.outerEdge.length - 1;
      const fi = w2s(box.innerEdge[li].x, box.innerEdge[li].y);
      const fo = w2s(box.outerEdge[lo].x, box.outerEdge[lo].y);
      ctx.moveTo(fi.x, fi.y);
      ctx.lineTo(fo.x, fo.y);
    }
  }
  ctx.stroke();

  // Back-side rail — white line along the outer edge (away from lane)
  // running the full row. Concatenates each box's outerEdge polyline,
  // skipping the shared boundary point between adjacent boxes.
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let bi = 0; bi < pb.boxes.length; bi++) {
    const outer = pb.boxes[bi].outerEdge;
    const startI = bi === 0 ? 0 : 1;
    for (let i = startI; i < outer.length; i++) {
      const s = w2s(outer[i].x, outer[i].y);
      if (bi === 0 && i === 0) ctx.moveTo(s.x, s.y);
      else                     ctx.lineTo(s.x, s.y);
    }
  }
  ctx.stroke();
}

// ---------- PIT MARKERS ----------
const PIT_MARKER_COLOR = '#4dd0e1';

// Visual specs for each pit-spline marker. Color carries the role:
//   PIT S/F         — white,  reserved for race-control / lap timing
//   PIT SPEED start — yellow, "slow down here"
//   PIT SPEED end   — yellow, paired with start, "back to racing pace"
// The checker pattern is reserved for the MAIN track's S/F line so
// pit markers stay visually distinct from race-control markings.
const PIT_SPLINE_MARKER_SPECS = {
  startFinish:    { color: '#f5f5f5', label: 'PIT S/F'     },
  speedZoneStart: { color: '#f5c441', label: 'PIT SPEED ▶' },
  speedZoneEnd:   { color: '#f5c441', label: '◀ PIT END'   },
};

function drawPitMarkers(ctx, state, w2s) {
  const pl = state.trackData.pitLane;
  if (!pl) return;
  const direction = state.trackData.direction ?? 'cw';
  if (pl.entry) drawOnePitMarker(ctx, state, w2s, 'entry', pl.entry, direction);
  if (pl.exit)  drawOnePitMarker(ctx, state, w2s, 'exit',  pl.exit,  direction);
  for (const name of Object.keys(PIT_SPLINE_MARKER_SPECS)) {
    if (pl[name]) drawPitSplineMarker(ctx, state, w2s, name, pl[name], pl);
  }

  if (state.mode === 'pitlane' && state.hover && state.hover.type === 'pit-marker-preview') {
    drawPitMarkerPreview(ctx, state, w2s, state.hover, direction);
  }
  if (state.mode === 'pitlane' && state.hover && state.hover.type === 'pit-spline-marker-preview') {
    drawPitSplineMarkerPreview(ctx, state, w2s, state.hover);
  }
}

// Solid perpendicular line across the pit lane spline, color +
// label per marker spec. Halo + thicker stroke when active /
// hovered to telegraph "this is the marker about to be edited".
function drawPitSplineMarker(ctx, state, w2s, name, marker, pl) {
  const spec = PIT_SPLINE_MARKER_SPECS[name];
  if (!spec) return;
  const ends = markerLineEndpoints(marker, pl, 'cw', false);
  if (!ends) return;
  const isActive = state.mode === 'pitlane' && state.activePitMarker === name;
  const isHover  = state.mode === 'pitlane' && state.hover
                && state.hover.type === 'pit-marker' && state.hover.marker === name;

  const lw = isActive ? 7 : 5;
  const sa = w2s(ends.a.x, ends.a.y);
  const sb = w2s(ends.b.x, ends.b.y);

  if (isActive || isHover) {
    ctx.strokeStyle = isActive ? 'rgba(229, 75, 60, 0.7)' : 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = lw + 4;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
  }

  ctx.strokeStyle = spec.color;
  ctx.lineWidth = lw;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
  ctx.stroke();

  drawMarkerLabel(ctx, state, w2s, ends.center, ends.perp, spec.label, spec.color);
}

// Ghost preview while hovering the pit-lane spline with any pit-
// spline marker active. Shape mirrors drawPitSplineMarker but with
// dashed semi-transparent strokes — placement isn't committed yet.
function drawPitSplineMarkerPreview(ctx, state, w2s, hover) {
  const pl = state.trackData.pitLane;
  if (!pl) return;
  const spec = PIT_SPLINE_MARKER_SPECS[hover.marker];
  if (!spec) return;
  const tempMarker = { segmentIdx: hover.segIdx, t: hover.t };
  const ends = markerLineEndpoints(tempMarker, pl, 'cw', false);
  if (!ends) return;
  const sa = w2s(ends.a.x, ends.a.y);
  const sb = w2s(ends.b.x, ends.b.y);
  ctx.strokeStyle = `${spec.color}99`;   // ~60 % opacity hex tail
  ctx.lineWidth = 5;
  ctx.lineCap = 'butt';
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawOnePitMarker(ctx, state, w2s, name, marker, direction) {
  const ends = markerLineEndpoints(marker, state.trackData.trackline, direction);
  if (!ends) return;
  const isActive = state.mode === 'pitlane' && state.activePitMarker === name;
  const isHover  = state.mode === 'pitlane' && state.hover && state.hover.type === 'pit-marker' && state.hover.marker === name;

  const sa = w2s(ends.a.x, ends.a.y);
  const sb = w2s(ends.b.x, ends.b.y);

  ctx.strokeStyle = '#0c0f12';
  ctx.lineWidth = (isActive ? 6 : 4) + 2;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
  ctx.stroke();

  ctx.strokeStyle = PIT_MARKER_COLOR;
  ctx.lineWidth = isActive ? 6 : 4;
  ctx.beginPath();
  ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
  ctx.stroke();

  if (isActive || isHover) {
    ctx.strokeStyle = isActive ? 'rgba(229, 75, 60, 0.7)' : 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = (isActive ? 6 : 4) + 4;
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
    ctx.strokeStyle = PIT_MARKER_COLOR;
    ctx.lineWidth = isActive ? 6 : 4;
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
  }

  drawMarkerLabel(ctx, state, w2s, ends.center, ends.perp, name === 'entry' ? 'PIT IN' : 'PIT OUT', PIT_MARKER_COLOR);
}

function drawPitMarkerPreview(ctx, state, w2s, hover, direction) {
  const tempMarker = { segmentIdx: hover.segIdx, t: hover.t };
  const ends = markerLineEndpoints(tempMarker, state.trackData.trackline, direction);
  if (!ends) return;
  const sa = w2s(ends.a.x, ends.a.y);
  const sb = w2s(ends.b.x, ends.b.y);
  ctx.strokeStyle = 'rgba(77, 208, 225, 0.65)';
  ctx.lineWidth = 5;
  ctx.lineCap = 'butt';
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
  ctx.stroke();
  ctx.setLineDash([]);
}

// ---------- MARKERS (S/F + start line + sectors) ----------
function drawMarkers(ctx, state, w2s) {
  const direction = state.trackData.direction ?? 'cw';
  if (state.trackData.sectors?.[0]) drawOneMarker(ctx, state, w2s, 'sector0', state.trackData.sectors[0], direction);
  if (state.trackData.sectors?.[1]) drawOneMarker(ctx, state, w2s, 'sector1', state.trackData.sectors[1], direction);
  if (state.trackData.startFinish)  drawOneMarker(ctx, state, w2s, 'startFinish', state.trackData.startFinish, direction);
  if (state.trackData.startLine)    drawOneMarker(ctx, state, w2s, 'startLine',   state.trackData.startLine, direction);

  if (state.mode === 'markers' && state.hover && state.hover.type === 'marker-preview') {
    drawMarkerPreview(ctx, state, w2s, state.hover, direction);
  }
}

function drawOneMarker(ctx, state, w2s, name, marker, direction) {
  const ends = markerLineEndpoints(marker, state.trackData.trackline, direction);
  if (!ends) return;
  const isActive = state.mode === 'markers' && state.activeMarker === name;
  const isHover  = state.mode === 'markers' && state.hover && state.hover.type === 'marker' && state.hover.marker === name;

  if (name === 'startFinish') {
    drawCheckeredLine(ctx, w2s, ends.a, ends.b, isActive ? 7 : 5);
  } else if (name === 'startLine') {
    const sa = w2s(ends.a.x, ends.a.y);
    const sb = w2s(ends.b.x, ends.b.y);
    ctx.strokeStyle = '#0c0f12';
    ctx.lineWidth = (isActive ? 7 : 5) + 2;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
    ctx.strokeStyle = '#6cdf6c';
    ctx.lineWidth = isActive ? 7 : 5;
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
  } else {
    const sectorIdx = name === 'sector0' ? 0 : 1;
    const color = SECTOR_COLORS[sectorIdx].line;
    const sa = w2s(ends.a.x, ends.a.y);
    const sb = w2s(ends.b.x, ends.b.y);
    ctx.strokeStyle = '#0c0f12';
    ctx.lineWidth = (isActive ? 6 : 4) + 2;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = isActive ? 6 : 4;
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
  }

  if (isActive || isHover) {
    const sa = w2s(ends.a.x, ends.a.y);
    const sb = w2s(ends.b.x, ends.b.y);
    const baseW = name === 'startFinish' || name === 'startLine' ? (isActive ? 7 : 5) : (isActive ? 6 : 4);
    ctx.strokeStyle = isActive ? 'rgba(229, 75, 60, 0.7)' : 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = baseW + 4;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
    if (name === 'startFinish') {
      drawCheckeredLine(ctx, w2s, ends.a, ends.b, isActive ? 7 : 5);
    } else if (name === 'startLine') {
      ctx.strokeStyle = '#6cdf6c';
      ctx.lineWidth = isActive ? 7 : 5;
      ctx.beginPath();
      ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
      ctx.stroke();
    } else {
      const sectorIdx = name === 'sector0' ? 0 : 1;
      ctx.strokeStyle = SECTOR_COLORS[sectorIdx].line;
      ctx.lineWidth = isActive ? 6 : 4;
      ctx.beginPath();
      ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
      ctx.stroke();
    }
  }

  let label, labelColor;
  if (name === 'startFinish')      { label = 'S/F';   labelColor = '#fff'; }
  else if (name === 'startLine')   { label = 'START'; labelColor = '#6cdf6c'; }
  else if (name === 'sector0')     { label = 'S1';    labelColor = SECTOR_COLORS[0].line; }
  else                              { label = 'S2';    labelColor = SECTOR_COLORS[1].line; }
  drawMarkerLabel(ctx, state, w2s, ends.center, ends.perp, label, labelColor);
}

function drawCheckeredLine(ctx, w2s, a, b, lineWidth) {
  const sa = w2s(a.x, a.y);
  const sb = w2s(b.x, b.y);
  const dx = sb.x - sa.x, dy = sb.y - sa.y;
  const lenPx = Math.hypot(dx, dy);
  const numChunks = Math.max(4, Math.round(lenPx / lineWidth));
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'butt';
  for (let i = 0; i < numChunks; i++) {
    const t1 = i / numChunks, t2 = (i + 1) / numChunks;
    ctx.strokeStyle = i % 2 === 0 ? '#fff' : '#0c0f12';
    ctx.beginPath();
    ctx.moveTo(sa.x + dx * t1, sa.y + dy * t1);
    ctx.lineTo(sa.x + dx * t2, sa.y + dy * t2);
    ctx.stroke();
  }
}

function drawMarkerLabel(ctx, state, w2s, center, perp, text, color) {
  const halfWidth = state.trackData.trackline.width / 2;
  const offset = 4;
  const lx = center.x + perp.x * (halfWidth + offset);
  const ly = center.y + perp.y * (halfWidth + offset);
  const s = w2s(lx, ly);
  ctx.font = '600 10px JetBrains Mono';
  ctx.fillStyle = '#0c0f12';
  ctx.strokeStyle = '#0c0f12';
  ctx.lineWidth = 3;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.strokeText(text, s.x, s.y);
  ctx.fillStyle = color;
  ctx.fillText(text, s.x, s.y);
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
}

function drawMarkerPreview(ctx, state, w2s, hover, direction) {
  const tempMarker = { segmentIdx: hover.segIdx, t: hover.t };
  const ends = markerLineEndpoints(tempMarker, state.trackData.trackline, direction);
  if (!ends) return;
  const sa = w2s(ends.a.x, ends.a.y);
  const sb = w2s(ends.b.x, ends.b.y);
  let color;
  if (hover.marker === 'startFinish')      color = 'rgba(255, 255, 255, 0.55)';
  else if (hover.marker === 'startLine')   color = 'rgba(108, 223, 108, 0.55)';
  else if (hover.marker === 'sector0')     color = 'rgba(77, 208, 225, 0.65)';
  else                                      color = 'rgba(255, 183, 77, 0.65)';
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.lineCap = 'butt';
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
  ctx.stroke();
  ctx.setLineDash([]);
}

// ---------- SURFACE PATCHES ----------
// Layer-aware patch render. `layer` is 'below' | 'merge' | 'above'
// (see shared/surfaces.js patchLayer for the mapping). Within a
// layer, patches are sorted ascending by SURFACE_PRIORITY; the
// active patch is skipped here and re-rendered later by
// drawActiveSurfacePatch so it always sits on top.
function drawSurfacePatches(ctx, state, w2s, layer) {
  const patches = state.trackData.surfacePatches || [];
  const activeIdx = state.mode === 'surfaces' ? state.activePatchIdx : -1;
  const order = patches
    .map((p, idx) => ({ p, idx }))
    .filter(x => x.idx !== activeIdx && patchLayer(x.p) === layer)
    .sort((a, b) => {
      const pa = SURFACE_PRIORITY[a.p.surface] ?? 99;
      const pb = SURFACE_PRIORITY[b.p.surface] ?? 99;
      return pa - pb;
    });
  for (const { p } of order) drawOnePatch(ctx, state, w2s, p, false);
}

// Render the active (selected) patch on top of all content layers.
// Editing visibility wins over natural stacking — a grass patch the
// user is editing should be visible even where the strip would
// normally cover it.
function drawActiveSurfacePatch(ctx, state, w2s) {
  const activeIdx = state.mode === 'surfaces' ? state.activePatchIdx : -1;
  if (activeIdx < 0) return;
  const patch = state.trackData.surfacePatches?.[activeIdx];
  if (patch) drawOnePatch(ctx, state, w2s, patch, true);
}

function drawOnePatch(ctx, state, w2s, patch, isActive) {
  const poly = patch.polygon;
  if (poly.length < 2) return;
  const colors = patchColors(patch);
  const closed = patch.closed !== false;
  const alpha = patch.alpha ?? 0.5;

  if (closed) {
    if (poly.length < 3) {
      const a = w2s(poly[0].x, poly[0].y);
      const b = w2s(poly[1].x, poly[1].y);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = isActive ? colors.stroke : dimColor(colors.stroke, 0.6);
      ctx.lineWidth = 1;
      ctx.stroke();
      return;
    }
    const dense = densifyClosedLoop(poly, 14, alpha);
    ctx.beginPath();
    const f = w2s(dense[0].x, dense[0].y);
    ctx.moveTo(f.x, f.y);
    for (let i = 1; i < dense.length; i++) {
      const s = w2s(dense[i].x, dense[i].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.closePath();

    ctx.fillStyle = isActive ? colors.fill : dimColor(colors.fill, 0.7);
    ctx.fill();
    ctx.strokeStyle = isActive ? colors.stroke : dimColor(colors.stroke, 0.6);
    ctx.lineWidth = isActive ? 1.5 : 1;
    ctx.stroke();
  } else {
    const thickness = patch.thickness ?? 8.0;
    const widthPx = Math.max(2, thickness * state.camera.zoom);
    const dense = densifyOpenPath(poly, 14, alpha);

    if (patch.surface === 'curb') {
      drawCurbStripes(ctx, state, w2s, dense, widthPx, thickness, isActive, patch.curbColor || 'red');
      if (isActive) {
        ctx.beginPath();
        const f = w2s(dense[0].x, dense[0].y);
        ctx.moveTo(f.x, f.y);
        for (let i = 1; i < dense.length; i++) {
          const s = w2s(dense[i].x, dense[i].y);
          ctx.lineTo(s.x, s.y);
        }
        ctx.strokeStyle = 'rgba(160, 165, 172, 0.7)';
        ctx.lineWidth = 1;
        ctx.lineCap = 'butt';
        ctx.stroke();
      }
      return;
    }

    ctx.beginPath();
    const f = w2s(dense[0].x, dense[0].y);
    ctx.moveTo(f.x, f.y);
    for (let i = 1; i < dense.length; i++) {
      const s = w2s(dense[i].x, dense[i].y);
      ctx.lineTo(s.x, s.y);
    }

    // End-cap is author-controlled for non-curb open bands. Unset =
    // 'round' (the historical default). 'butt' / 'square' let curbs
    // and runoff strips end flush with the last waypoint.
    const endCap = patch.endCap || 'round';
    ctx.strokeStyle = isActive ? colors.fill : dimColor(colors.fill, 0.7);
    ctx.lineWidth = widthPx;
    ctx.lineCap = endCap;
    ctx.lineJoin = 'round';
    ctx.stroke();

    if (isActive) {
      ctx.strokeStyle = colors.stroke;
      ctx.lineWidth = 1;
      ctx.lineCap = 'butt';
      ctx.stroke();
    }
  }
}

// Resolve a patch's editor fill/stroke. Tarmac patches can carry a
// `tarmacColor` (TARMAC_COLORS) — convert the hex to translucent
// rgba so the preview keeps the same "see-through" feel as default
// SURFACE_COLORS entries.
function patchColors(patch) {
  if (patch.surface === 'tarmac' && patch.tarmacColor && TARMAC_COLORS[patch.tarmacColor]) {
    const hex = TARMAC_COLORS[patch.tarmacColor];
    return {
      fill:   hexWithAlpha(hex, 0.55),
      stroke: hexWithAlpha(hex, 0.85),
    };
  }
  return SURFACE_COLORS[patch.surface] || SURFACE_COLORS.grass;
}

// Red curbs alternate red/white chunks (Monaco / classic F1). Other
// colors render as a solid band — drivers wanted yellow/blue/grey
// accents without the candy-stripe pattern. The solid path skips the
// chunking loop entirely and just strokes the full polyline.
function drawCurbStripes(ctx, state, w2s, dense, widthPx, thickness, isActive, curbColor = 'red') {
  const baseHex = CURB_COLORS[curbColor] || CURB_COLORS.red;
  const colorBase = isActive ? baseHex : hexWithAlpha(baseHex, 0.7);

  ctx.lineWidth = widthPx;
  ctx.lineCap   = 'butt';
  ctx.lineJoin  = 'miter';
  ctx.miterLimit = 4;

  if (curbColor !== 'red') {
    ctx.strokeStyle = colorBase;
    ctx.beginPath();
    const s0 = w2s(dense[0].x, dense[0].y);
    ctx.moveTo(s0.x, s0.y);
    for (let i = 1; i < dense.length; i++) {
      const s = w2s(dense[i].x, dense[i].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
    return;
  }

  const chunkLenM = Math.max(0.4, thickness);
  const colorWhite = isActive ? 'rgba(245, 245, 245, 0.95)' : 'rgba(245, 245, 245, 0.65)';

  let chunkStart = 0;
  let accumulated = 0;
  let chunkIdx = 0;

  const strokeChunk = (from, to, idx) => {
    if (to <= from) return;
    ctx.strokeStyle = idx % 2 === 0 ? colorWhite : colorBase;
    ctx.beginPath();
    const s0 = w2s(dense[from].x, dense[from].y);
    ctx.moveTo(s0.x, s0.y);
    for (let j = from + 1; j <= to; j++) {
      const s = w2s(dense[j].x, dense[j].y);
      ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
  };

  for (let i = 1; i < dense.length; i++) {
    accumulated += Math.hypot(dense[i].x - dense[i - 1].x, dense[i].y - dense[i - 1].y);
    const isLast = i === dense.length - 1;
    if (accumulated >= chunkLenM || isLast) {
      strokeChunk(chunkStart, i, chunkIdx);
      chunkStart = i;
      accumulated = 0;
      chunkIdx++;
    }
  }
}

// ---------- WAYPOINTS ----------
function drawWaypoints(ctx, state, w2s) {
  if (!hasActiveEntity(state)) return;
  const wps = activeWaypoints(state);
  ctx.font = '500 9px JetBrains Mono';

  for (let i = 0; i < wps.length; i++) {
    const wp = wps[i];
    const s = w2s(wp.x, wp.y);
    const sharp = !!wp.sharp;
    const sel = isSelected(state, i);
    const hov = !state.drag && state.hover && state.hover.type === 'waypoint' && state.hover.index === i;

    if (sel || hov) {
      ctx.fillStyle = sel ? 'rgba(229, 75, 60, 0.25)' : 'rgba(255, 255, 255, 0.15)';
      ctx.beginPath();
      ctx.arc(s.x, s.y, 14, 0, 2 * Math.PI);
      ctx.fill();
    }

    ctx.fillStyle   = sel ? '#fff' : '#e54b3c';
    ctx.strokeStyle = '#0c0f12';
    ctx.lineWidth = 2;
    if (sharp) {
      const r = sel ? 6.5 : 5.5;
      ctx.fillRect(s.x - r, s.y - r, r * 2, r * 2);
      ctx.strokeRect(s.x - r, s.y - r, r * 2, r * 2);
    } else {
      ctx.beginPath();
      ctx.arc(s.x, s.y, sel ? 6 : 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    ctx.fillStyle = '#6c7378';
    ctx.fillText(String(i), s.x + 9, s.y - 8);
  }
}

// ---------- INTERACTION OVERLAYS (no-op when state fields are null) ----------
function drawMarquee(ctx, state) {
  if (!state.drag || state.drag.type !== 'marquee') return;
  const s = state.drag.startScreen, e = state.drag.endScreen;
  const x = Math.min(s.x, e.x), y = Math.min(s.y, e.y);
  const w = Math.abs(e.x - s.x), h = Math.abs(e.y - s.y);
  if (w < 2 && h < 2) return;
  ctx.fillStyle = 'rgba(229, 75, 60, 0.08)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = 'rgba(229, 75, 60, 0.7)';
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
}

function drawHover(ctx, state, w2s) {
  if (state.drag) return;
  if (!state.hover || state.hover.type !== 'segment') return;

  const s = w2s(state.hover.point.x, state.hover.point.y);
  ctx.fillStyle = '#6cdf6c';
  ctx.strokeStyle = '#0c0f12';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(s.x, s.y, 6, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();

  ctx.strokeStyle = '#0c0f12';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(s.x - 2.5, s.y); ctx.lineTo(s.x + 2.5, s.y);
  ctx.moveTo(s.x, s.y - 2.5); ctx.lineTo(s.x, s.y + 2.5);
  ctx.stroke();
}

function drawSnapTarget(ctx, state, w2s) {
  if (!state.snapTarget) return;
  if (state.drag) return;
  const s = w2s(state.snapTarget.x, state.snapTarget.y);
  ctx.strokeStyle = '#6cdf6c';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(s.x, s.y, 7, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(s.x - 10, s.y); ctx.lineTo(s.x + 10, s.y);
  ctx.moveTo(s.x, s.y - 10); ctx.lineTo(s.x, s.y + 10);
  ctx.stroke();
}

function drawCrosshair(ctx, state, canvas) {
  if (!state.cursor.inCanvas) return;
  if (state.drag && state.drag.type === 'pan') return;
  ctx.strokeStyle = 'rgba(108, 115, 120, 0.18)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(state.cursor.x, 0);
  ctx.lineTo(state.cursor.x, canvas.height);
  ctx.moveTo(0, state.cursor.y);
  ctx.lineTo(canvas.width, state.cursor.y);
  ctx.stroke();
}

function drawCalibrationOverlay(ctx, state, canvas, w2s) {
  if (!state.calibrationMode) return;

  const drawCrosshairAt = (worldP, label) => {
    const s = w2s(worldP.x, worldP.y);
    ctx.strokeStyle = '#6cdf6c';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(s.x, s.y, 8, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(s.x - 12, s.y); ctx.lineTo(s.x + 12, s.y);
    ctx.moveTo(s.x, s.y - 12); ctx.lineTo(s.x, s.y + 12);
    ctx.stroke();
    if (label) {
      ctx.font = '600 10px JetBrains Mono';
      ctx.fillStyle = '#6cdf6c';
      ctx.fillText(label, s.x + 14, s.y - 6);
    }
  };

  if (state.calibrationPoint1) {
    drawCrosshairAt(state.calibrationPoint1, 'A');
    if (state.cursor.inCanvas) {
      ctx.strokeStyle = 'rgba(108, 223, 108, 0.55)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      const s1 = w2s(state.calibrationPoint1.x, state.calibrationPoint1.y);
      ctx.beginPath();
      ctx.moveTo(s1.x, s1.y);
      ctx.lineTo(state.cursor.x, state.cursor.y);
      ctx.stroke();
      ctx.setLineDash([]);

      const w = screenToWorld(state, canvas, state.cursor.x, state.cursor.y);
      const dist = Math.hypot(w.x - state.calibrationPoint1.x, w.y - state.calibrationPoint1.y);
      ctx.font = '500 10px JetBrains Mono';
      ctx.fillStyle = '#6cdf6c';
      ctx.fillText(`${dist.toFixed(1)} m (current scale)`, state.cursor.x + 14, state.cursor.y + 18);
    }
  }
}

// ---------- LABELS ----------
//
// Two label kinds, both anchored on the trackline:
//   corner — small numbered/textured badge offset perpendicular to
//            the track at (segmentIdx, t).
//   area   — bracketed banner spanning [start → end] with a name
//            tag at the midpoint. The brackets are short ticks at
//            the start/end positions; the banner sits offset
//            perpendicular from the track midpoint, which gives a
//            stable anchor even when the area wraps S/F.
//
// Stays visible in all modes but dims outside `labels` mode so it
// reads as reference rather than active editing chrome. Hit zones
// must align with what we paint here — see hit-tests.js
// LABEL_BADGE_OFFSET_M (kept in sync).

const LABEL_BADGE_OFFSET_M = 12;
const LABEL_AREA_BANNER_OFFSET_M = 14;
const LABEL_AREA_TICK_LEN_M = 6;

function drawLabels(ctx, state, w2s) {
  const td = state.trackData;
  const labels = td.labels || [];
  if (labels.length === 0 && !state.pendingLabel) return;
  const wps = td.trackline?.waypoints || [];
  if (wps.length < 3) return;
  const alpha = td.trackline.alpha ?? 0.5;
  const inMode = state.mode === 'labels';
  const baseAlpha = inMode ? 1 : 0.45;

  for (let i = 0; i < labels.length; i++) {
    const L = labels[i];
    if (!L) continue;
    const isActive = inMode && state.activeLabelIdx === i;
    if (L.kind === 'corner') {
      drawCornerLabel(ctx, w2s, wps, alpha, L, isActive, baseAlpha);
    } else if (L.kind === 'area') {
      drawAreaLabel(ctx, w2s, wps, alpha, L, isActive, baseAlpha);
    }
  }

  // Pending placement preview — start tick already-placed for
  // areas, and a hint tooltip near the cursor explaining what the
  // next click does.
  if (inMode && state.pendingLabel) {
    drawPendingLabel(ctx, state, w2s, wps, alpha);
  }
}

function drawCornerLabel(ctx, w2s, wps, alpha, label, isActive, baseAlpha) {
  const p = curvePointAt(wps, label.segmentIdx, label.t, alpha);
  const perp = trackPerpendicularAt(wps, label.segmentIdx, label.t, alpha);
  const cx = p.x + perp.x * LABEL_BADGE_OFFSET_M;
  const cy = p.y + perp.y * LABEL_BADGE_OFFSET_M;
  const sCenter = w2s(cx, cy);
  const sAnchor = w2s(p.x, p.y);

  // Connector line from track edge to badge.
  ctx.save();
  ctx.globalAlpha = baseAlpha;
  ctx.strokeStyle = isActive ? '#ffae3b' : '#9ec5ff';
  ctx.lineWidth = isActive ? 2 : 1;
  ctx.setLineDash(isActive ? [] : [3, 3]);
  ctx.beginPath();
  ctx.moveTo(sAnchor.x, sAnchor.y);
  ctx.lineTo(sCenter.x, sCenter.y);
  ctx.stroke();
  ctx.setLineDash([]);

  // Badge — circle filled with the editor's surface palette so it
  // reads against tarmac and grass alike.
  const r = isActive ? 13 : 11;
  ctx.fillStyle = isActive ? '#ffae3b' : 'rgba(20, 24, 28, 0.92)';
  ctx.strokeStyle = isActive ? '#fff' : '#9ec5ff';
  ctx.lineWidth = isActive ? 2 : 1.4;
  ctx.beginPath();
  ctx.arc(sCenter.x, sCenter.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.font = `${isActive ? 700 : 600} ${isActive ? 13 : 12}px JetBrains Mono`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = isActive ? '#0c0f12' : '#fff';
  ctx.fillText(label.text || '?', sCenter.x, sCenter.y + 0.5);
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

function drawAreaLabel(ctx, w2s, wps, alpha, label, isActive, baseAlpha) {
  const startP = curvePointAt(wps, label.startSegIdx, label.startT, alpha);
  const endP   = curvePointAt(wps, label.endSegIdx,   label.endT,   alpha);
  const startPerp = trackPerpendicularAt(wps, label.startSegIdx, label.startT, alpha);
  const endPerp   = trackPerpendicularAt(wps, label.endSegIdx,   label.endT,   alpha);

  const color = isActive ? '#ffae3b' : '#9ec5ff';

  ctx.save();
  ctx.globalAlpha = baseAlpha;
  // Start + end ticks: short perpendicular bars at each boundary.
  ctx.strokeStyle = color;
  ctx.lineWidth = isActive ? 2.5 : 1.8;
  ctx.lineCap = 'butt';
  for (const [pt, perp] of [[startP, startPerp], [endP, endPerp]]) {
    const tipX = pt.x + perp.x * LABEL_AREA_TICK_LEN_M;
    const tipY = pt.y + perp.y * LABEL_AREA_TICK_LEN_M;
    const sa = w2s(pt.x, pt.y);
    const sb = w2s(tipX, tipY);
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y);
    ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
  }

  // Banner: text pill anchored at the start position offset
  // perpendicular. Positioned on the start side rather than midpoint
  // so wraparound areas (start near end of lap, end past S/F) still
  // anchor visibly.
  const bannerX = startP.x + startPerp.x * LABEL_AREA_BANNER_OFFSET_M;
  const bannerY = startP.y + startPerp.y * LABEL_AREA_BANNER_OFFSET_M;
  const sBanner = w2s(bannerX, bannerY);

  ctx.font = `${isActive ? 700 : 600} ${isActive ? 12 : 11}px JetBrains Mono`;
  const text = label.text || 'Unnamed area';
  const metrics = ctx.measureText(text);
  const padX = 8, padY = 5;
  const w = metrics.width + padX * 2;
  const h = (isActive ? 14 : 13) + padY * 2;

  ctx.fillStyle = isActive ? '#ffae3b' : 'rgba(20, 24, 28, 0.92)';
  ctx.strokeStyle = color;
  ctx.lineWidth = isActive ? 2 : 1.4;
  // Pill is centered horizontally on the anchor for legibility.
  const pillX = sBanner.x - w / 2;
  const pillY = sBanner.y - h / 2;
  ctx.beginPath();
  ctx.rect(pillX, pillY, w, h);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = isActive ? '#0c0f12' : '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, sBanner.x, sBanner.y + 0.5);
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

function drawPendingLabel(ctx, state, w2s, wps, alpha) {
  const pl = state.pendingLabel;
  if (!pl) return;

  // The hover slot carries the projected (segIdx, t, point) under the
  // cursor — use it for ghost previews so the preview lands on the
  // trackline (where the click commits) rather than at the cursor.
  const preview = (state.hover && state.hover.type === 'label-preview') ? state.hover : null;

  // For an area mid-placement, paint the start tick so the user can
  // see what they've already committed.
  if (pl.kind === 'area' && pl.startSegIdx != null) {
    const p = curvePointAt(wps, pl.startSegIdx, pl.startT, alpha);
    const perp = trackPerpendicularAt(wps, pl.startSegIdx, pl.startT, alpha);
    const tipX = p.x + perp.x * LABEL_AREA_TICK_LEN_M;
    const tipY = p.y + perp.y * LABEL_AREA_TICK_LEN_M;
    const sa = w2s(p.x, p.y);
    const sb = w2s(tipX, tipY);
    ctx.save();
    ctx.strokeStyle = '#ffae3b';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y);
    ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
    // Dashed line from start tick to the projected end-preview on
    // the trackline. Lands precisely on the trackline (where the
    // click will commit) rather than at the cursor.
    if (preview) {
      const ep = w2s(preview.point.x, preview.point.y);
      ctx.strokeStyle = 'rgba(255, 174, 59, 0.55)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(sb.x, sb.y);
      ctx.lineTo(ep.x, ep.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }

  // Ghost preview at the projected centerline point — the user sees
  // exactly where the click will commit, regardless of cursor offset
  // from the line.
  if (preview) {
    const projPerp = trackPerpendicularAt(wps, preview.segIdx, preview.t, alpha);
    const sCenter = w2s(preview.point.x, preview.point.y);
    ctx.save();
    if (pl.kind === 'corner') {
      // Ghost badge at the offset position the committed corner
      // would occupy.
      const cx = preview.point.x + projPerp.x * LABEL_BADGE_OFFSET_M;
      const cy = preview.point.y + projPerp.y * LABEL_BADGE_OFFSET_M;
      const sBadge = w2s(cx, cy);
      ctx.strokeStyle = 'rgba(255, 174, 59, 0.65)';
      ctx.fillStyle   = 'rgba(20, 24, 28, 0.55)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(sCenter.x, sCenter.y);
      ctx.lineTo(sBadge.x, sBadge.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(sBadge.x, sBadge.y, 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else if (pl.kind === 'area') {
      // Ghost tick at the projected point.
      const tipX = preview.point.x + projPerp.x * LABEL_AREA_TICK_LEN_M;
      const tipY = preview.point.y + projPerp.y * LABEL_AREA_TICK_LEN_M;
      const sTip = w2s(tipX, tipY);
      ctx.strokeStyle = 'rgba(255, 174, 59, 0.75)';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(sCenter.x, sCenter.y);
      ctx.lineTo(sTip.x, sTip.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // Small filled dot on the line itself so the user always sees the
    // exact point the click commits to.
    ctx.fillStyle = '#ffae3b';
    ctx.beginPath();
    ctx.arc(sCenter.x, sCenter.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Cursor hint badge — tells the user what the next click does.
  if (state.cursor.inCanvas) {
    let hint;
    if (pl.kind === 'corner') hint = 'Click track to place corner';
    else if (pl.kind === 'area' && pl.startSegIdx == null) hint = 'Click track for area START';
    else hint = 'Click track for area END';
    ctx.save();
    ctx.font = '500 10px JetBrains Mono';
    const metrics = ctx.measureText(hint);
    const padX = 6, padY = 3;
    const w = metrics.width + padX * 2, h = 14 + padY * 2;
    const x = state.cursor.x + 14;
    const y = state.cursor.y + 14;
    ctx.fillStyle = 'rgba(20, 24, 28, 0.92)';
    ctx.strokeStyle = '#ffae3b';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#ffae3b';
    ctx.textBaseline = 'middle';
    ctx.fillText(hint, x + padX, y + h / 2);
    ctx.textBaseline = 'alphabetic';
    ctx.restore();
  }
}
